import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import UserDetailsDialog from '../../components/UserDetailsDialog';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'driver', label: 'Driver' },
  { value: 'manager', label: 'Manager' },
];

const PORTAL_ROLES = ['customer', 'driver', 'manager'];

const rolesMissing = (u) =>
  PORTAL_ROLES.filter((r) => !(u.accessible_roles || []).includes(r));

const UserAdmin = () => {
  const { user: currentUser, fetchUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    role: 'customer',
    is_active: true,
    is_staff: false,
    is_superuser: false,
  });
  const [saving, setSaving] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [addRoleUser, setAddRoleUser] = useState(null);
  const [addRoleForm, setAddRoleForm] = useState({
    role: 'customer',
    parent_customer_id: '',
    company_name: '',
  });
  const [removeRoleConfirm, setRemoveRoleConfirm] = useState(null);
  const [detailsUser, setDetailsUser] = useState(null);

  const loadUsers = async () => {
    setError('');
    try {
      const res = await api.get('/auth/admin/users/');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.error || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const openAddRole = (u) => {
    const missing = rolesMissing(u);
    setAddRoleUser(u);
    setAddRoleForm({
      role: missing[0] || 'customer',
      parent_customer_id: '',
      company_name: '',
    });
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      role: u.role,
      is_active: u.is_active,
      is_staff: !!u.is_staff,
      is_superuser: !!u.is_superuser,
    });
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    setError('');
    try {
      await api.patch(`/auth/admin/users/${editUser.id}/`, editForm);
      await loadUsers();
      if (editUser.id === currentUser?.id) {
        await fetchUser();
      }
      setEditUser(null);
    } catch (e) {
      const d = e.response?.data;
      setError(typeof d === 'object' ? JSON.stringify(d) : d || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setSaving(true);
    setError('');
    try {
      await api.delete(`/auth/admin/users/${deleteUser.id}/`);
      await loadUsers();
      setDeleteUser(null);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRole = async () => {
    if (!addRoleUser) return;
    setSaving(true);
    setError('');
    try {
      const body = { role: addRoleForm.role };
      if (addRoleForm.role === 'customer') {
        if (addRoleForm.parent_customer_id.trim()) {
          body.parent_customer_id = addRoleForm.parent_customer_id.trim();
        } else if (addRoleForm.company_name.trim()) {
          body.company_name = addRoleForm.company_name.trim();
        }
      }
      await api.post(`/auth/admin/users/${addRoleUser.id}/add-role/`, body);
      await loadUsers();
      if (addRoleUser.id === currentUser?.id) {
        await fetchUser();
      }
      setAddRoleUser(null);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Add role failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async () => {
    if (!removeRoleConfirm) return;
    const { user: u, role } = removeRoleConfirm;
    setSaving(true);
    setError('');
    try {
      await api.post(`/auth/admin/users/${u.id}/remove-role/`, { role });
      await loadUsers();
      if (u.id === currentUser?.id) {
        await fetchUser();
      }
      setRemoveRoleConfirm(null);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || 'Remove role failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
          Portal users (superuser)
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Add or remove portal access (customer / driver / manager profiles). Edit user sets the primary
          User.role field and Django flags. Removing a portal deletes that profile when safe; remove
          manager access before driver.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Typography>Loading…</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Primary role</TableCell>
                    <TableCell>Portals</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Staff</TableCell>
                    <TableCell>Superuser</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => (
                    <TableRow
                      key={u.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setDetailsUser(u)}
                    >
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                          {(u.accessible_roles || []).map((r) => (
                            <Chip
                              key={r}
                              label={r}
                              size="small"
                              variant="outlined"
                              onDelete={() => setRemoveRoleConfirm({ user: u, role: r })}
                            />
                          ))}
                          {rolesMissing(u).length > 0 && (
                            <Tooltip title="Add portal profile">
                              <IconButton size="small" onClick={() => openAddRole(u)} aria-label="add portal role">
                                <PersonAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{u.is_active ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{u.is_staff ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{u.is_superuser ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(u)} aria-label="edit user">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete user">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={u.id === currentUser?.id}
                              onClick={() => setDeleteUser(u)}
                              aria-label="delete user"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <UserDetailsDialog
          open={!!detailsUser}
          userId={detailsUser?.id}
          initialUser={detailsUser}
          onClose={() => setDetailsUser(null)}
          onEdit={openEdit}
        />

        <Dialog
          open={!!addRoleUser}
          onClose={() => !saving && setAddRoleUser(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add portal access</DialogTitle>
          <DialogContent>
            {addRoleUser && (
              <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {addRoleUser.email}
                </Typography>
                <TextField
                  select
                  label="Portal to add"
                  value={addRoleForm.role}
                  onChange={(e) => setAddRoleForm((f) => ({ ...f, role: e.target.value }))}
                  fullWidth
                >
                  {rolesMissing(addRoleUser).map((r) => (
                    <MenuItem key={r} value={r}>
                      {ROLE_OPTIONS.find((o) => o.value === r)?.label || r}
                    </MenuItem>
                  ))}
                </TextField>
                {addRoleForm.role === 'customer' && (
                  <>
                    <TextField
                      label="Primary company ID (team member)"
                      value={addRoleForm.parent_customer_id}
                      onChange={(e) => setAddRoleForm((f) => ({ ...f, parent_customer_id: e.target.value }))}
                      fullWidth
                      helperText="Optional. UUID of an existing primary company — user joins as team member. Leave blank to create a new standalone company (placeholder address)."
                      placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                    />
                    <TextField
                      label="Company name (new primary only)"
                      value={addRoleForm.company_name}
                      onChange={(e) => setAddRoleForm((f) => ({ ...f, company_name: e.target.value }))}
                      fullWidth
                      helperText="If not linking to a company above, optionally set the new account name."
                    />
                  </>
                )}
                {addRoleForm.role === 'manager' && (
                  <Alert severity="info">
                    Creates a driver profile (stub license) if needed, then manager access.
                  </Alert>
                )}
                {addRoleForm.role === 'driver' && (
                  <Alert severity="info">
                    Creates a driver profile with placeholder license data — update in the driver portal or
                    manager UI.
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddRoleUser(null)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleAddRole} disabled={saving}>
              {saving ? 'Saving…' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!removeRoleConfirm} onClose={() => !saving && setRemoveRoleConfirm(null)}>
          <DialogTitle>Remove portal access?</DialogTitle>
          <DialogContent>
            <Typography>
              Remove <strong>{removeRoleConfirm?.role}</strong> access from{' '}
              <strong>{removeRoleConfirm?.user?.email}</strong>? This deletes the linked profile when
              allowed (customer row blocked if jobs/invoices exist; remove manager before driver).
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveRoleConfirm(null)} disabled={saving}>
              Cancel
            </Button>
            <Button color="warning" variant="contained" onClick={handleRemoveRole} disabled={saving}>
              {saving ? 'Removing…' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!editUser} onClose={() => !saving && setEditUser(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit user</DialogTitle>
          <DialogContent>
            {editUser && (
              <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {editUser.email}
                </Typography>
                <TextField
                  select
                  label="Primary role (User.role)"
                  value={editForm.role}
                  onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                  fullWidth
                >
                  {ROLE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm((f) => ({ ...f, is_active: e.target.checked }))}
                    />
                  }
                  label="Active (can log in)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.is_staff}
                      onChange={(e) => setEditForm((f) => ({ ...f, is_staff: e.target.checked }))}
                    />
                  }
                  label="Staff (Django admin access)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.is_superuser}
                      onChange={(e) => setEditForm((f) => ({ ...f, is_superuser: e.target.checked }))}
                    />
                  }
                  label="Superuser"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUser(null)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!deleteUser} onClose={() => !saving && setDeleteUser(null)}>
          <DialogTitle>Delete user?</DialogTitle>
          <DialogContent>
            <Typography>
              Permanently delete <strong>{deleteUser?.email}</strong>? Related profiles may be removed by
              the database (cascade).
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteUser(null)} disabled={saving}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default UserAdmin;
